/**
 * 🚀 [Phase v13.52 — 2026-07-16] Sujet 2 — KPI Coût employeur moyen pondéré (saison)
 *
 * Composant KPI standalone affiché dans la section « Analyses » de la page Production.
 * Rafraîchi à chaque montage (pas de cache). Owner/Admin/CP uniquement (permission
 * gérée côté backend — HTTP 403 sinon).
 *
 * Contenu :
 *   - Grand chiffre en $/h (moyenne pondérée)
 *   - Sous-titre : saison + date de début
 *   - Info secondaire : X employés · Y heures travaillées
 *   - Tooltip explicatif de la pondération
 *   - Fallback FR-CA si l'endpoint échoue ou renvoie 0 heures
 */

import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { DollarSign, HelpCircle, Users, Clock } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MOIS_FR = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

function formatDateFR(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${parseInt(d, 10)} ${MOIS_FR[parseInt(m, 10) - 1] || ""} ${y}`;
}

function formatSaisonLabel(saison, annee) {
  const nom = saison === "ete" ? "Été" : saison === "hiver" ? "Hiver" : "Saison";
  return `${nom} ${annee}`;
}

export function KpiCoutEmployeurMoyenPondere() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${API_URL}/api/analyse/cout-employeur-moyen-pondere`)
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("[KpiCoutEmployeurMoyenPondere] fetch error:", err);
          setError(err?.response?.data?.detail || "Erreur de chargement");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // ── États dégradés ────────────────────────────────────────────────────
  if (loading) {
    return (
      <Card data-testid="kpi-cout-employeur-moyen-pondere" className="border-indigo-100">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 text-slate-700">
            <DollarSign className="w-4 h-4 text-indigo-600" />
            Coût employeur moyen pondéré
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-2">
            <div className="h-8 w-32 bg-slate-200 rounded" />
            <div className="h-3 w-48 bg-slate-100 rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card data-testid="kpi-cout-employeur-moyen-pondere" className="border-red-100">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 text-red-700">
            <DollarSign className="w-4 h-4" />
            Coût employeur moyen pondéré
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 italic" data-testid="kpi-cout-error">
            Non disponible pour le moment.
          </p>
        </CardContent>
      </Card>
    );
  }

  const cout = data?.cout_moyen_pondere ?? 0;
  const hasData = cout > 0 && (data?.total_heures || 0) > 0;
  const saisonLabel = formatSaisonLabel(data?.saison, data?.periode?.debut?.slice(0, 4));
  const debutFR = formatDateFR(data?.periode?.debut);
  const nbEmp = data?.nb_employes_inclus || 0;
  const totalHeures = data?.total_heures || 0;
  const nbExclus = data?.nb_punchs_exclus_sans_snapshot || 0;

  return (
    <Card data-testid="kpi-cout-employeur-moyen-pondere" className="border-indigo-100 bg-gradient-to-br from-white to-indigo-50/30">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between gap-2 text-slate-700">
          <span className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-indigo-600" />
            Coût employeur moyen pondéré
          </span>
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="text-slate-400 hover:text-indigo-600 transition-colors"
                  data-testid="kpi-cout-tooltip-trigger"
                  aria-label="Explication de la formule"
                >
                  <HelpCircle className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="left"
                className="max-w-xs text-xs leading-relaxed"
                data-testid="kpi-cout-tooltip"
              >
                Ce coût est calculé en pondérant chaque taux horaire par le nombre
                d&apos;heures réellement travaillées depuis le début de la saison. Il
                reflète le vrai coût moyen actuel de l&apos;équipe terrain.
                <br /><br />
                <span className="italic text-slate-300">
                  Formule : Σ (taux × heures) / Σ heures.
                  Source : ccq_snapshot (lecture seule).
                </span>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {hasData ? (
          <>
            <div className="flex items-baseline gap-2">
              <span
                className="text-3xl font-bold text-indigo-700 tabular-nums"
                data-testid="kpi-cout-value"
              >
                {cout.toFixed(2)} $
              </span>
              <span className="text-sm text-slate-500">/h</span>
            </div>
            <p className="text-sm text-slate-600" data-testid="kpi-cout-subtitle">
              {saisonLabel} · Depuis le {debutFR}
            </p>
            <div className="flex items-center gap-4 text-xs text-slate-500 pt-1">
              <span className="flex items-center gap-1" data-testid="kpi-cout-nb-emp">
                <Users className="w-3 h-3" /> {nbEmp} employé{nbEmp > 1 ? "s" : ""}
              </span>
              <span className="flex items-center gap-1" data-testid="kpi-cout-nb-heures">
                <Clock className="w-3 h-3" /> {totalHeures.toLocaleString("fr-CA")} h travaillées
              </span>
            </div>
            {nbExclus > 0 && (
              <Badge
                variant="outline"
                className="text-[10px] mt-1 border-amber-300 text-amber-700"
                data-testid="kpi-cout-badge-exclus"
              >
                {nbExclus} punch(s) ignoré(s) — sans snapshot CCQ
              </Badge>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-slate-500 italic" data-testid="kpi-cout-no-data">
              Aucune donnée exploitable pour {saisonLabel.toLowerCase()}.
            </p>
            {nbExclus > 0 && (
              <p className="text-xs text-slate-400" data-testid="kpi-cout-no-data-details">
                {nbExclus} punch(s) trouvé(s) sur la période mais aucun ne comporte de snapshot CCQ.
                Les nouveaux punchs seront correctement inclus.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
